import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Mic, Scan, Shield, Heart, Package, PenLine, Volume2, ChevronDown, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import MessagePreview from '../components/MessagePreview';
import { isPreviewEnv } from '../lib/previewEnv';

/* Premium packaging upgrade is temporarily hidden from customers until the
   real premium packaging is ready. All state, prices, cart logic, Stripe
   product (`letters-that-last-premium`) and DB values remain intact so the
   feature can be restored simply by flipping this flag back to true. */
const SHOW_PREMIUM_PACKAGING = false;

/* Memory Vault add-on in the Letters That Last™ configurator is temporarily
   hidden. All state (`vaultAddon`, `previewUnlocked`), the £29.99 cart line,
   the Stripe product id `memory-vault-addon`, backend PRODUCTS entry, DB
   fields, storage buckets and all vault routes remain intact. Flip this flag
   back to true to restore. The standalone /memory-vault landing page is
   unaffected either way. */
const SHOW_VAULT_ADDON = false;

/* Future-product marketing (Memory Vault promo card, "Arriving Soon" nav
   dropdown, Memory Vault nav links, 10/25-year storage claims) is hidden
   for the Letters That Last™-only public launch. The underlying components,
   routes, and feature flags remain intact so these experiences can be
   restored later. Flip this flag to re-enable all of them in one place. */
const SHOW_FUTURE_PRODUCTS = false;

/* ── Design System (from design_guidelines.json) ── */
const C = {
  bg: '#061017',
  surface: '#0a1924',
  surfaceEl: '#102434',
  overlay: 'rgba(6, 16, 23, 0.7)',
  text: '#fdfbf7',
  textSec: '#a8b6c1',
  gold: '#C6A96B',
  goldHover: '#D4B87A',
  goldMuted: 'rgba(198, 169, 107, 0.15)',
  goldRich: '#a8903a',
  btnText: '#3A342E',
  midnight: '#0d1f2d',
  cream: '#F5F1E8',
  darkText: '#2C2C2C',
  darkTextSec: '#5A5A5A',
};

const IMAGES = {
  product: 'https://static.prod-images.emergentagent.com/jobs/f1ab86e4-25d7-4e9f-98fe-cd05f946871b/images/43e346a87fd0782f1ebf7d1b7187edfcd6e7a84d8accd446d5048ffa54b99c6c.png',
  hero: '/ltl-hero.jpg',
  qr: 'https://images.unsplash.com/photo-1772048202308-f6e2cf963ca2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxnb2xkJTIwUVIlMjBjb2RlJTIwbHV4dXJ5fGVufDB8fHx8MTc3NTM3NzczM3ww&ixlib=rb-4.1.0&q=85',
  upload: 'https://images.unsplash.com/photo-1767334850102-b37f04d4e2b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxzbWFydHBob25lJTIwaGFuZCUyMGRpbWx5JTIwbGl0JTIwZW1vdGlvbmFsfGVufDB8fHx8MTc3NTM3NzczM3ww&ixlib=rb-4.1.0&q=85',
  texture: 'https://images.unsplash.com/photo-1766941288512-94a057e71d43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwbWlkbmlnaHQlMjBibHVlJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzUzNzc3MzN8MA&ixlib=rb-4.1.0&q=85',
};

const fadeUp = {
  initial: { y: 20, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const stagger = (delay = 0) => ({
  ...fadeUp,
  transition: { ...fadeUp.transition, delay },
});

/* ── Component ── */
const LettersLandingPage = () => {
  const { addItem, setIsOpen, totalItems } = useCart();
  const navigate = useNavigate();
  const [msgChoice, setMsgChoice] = useState('Neutral');
  const [colourChoice, setColourChoice] = useState(null);
  const [premiumPackaging, setPremiumPackaging] = useState(false);
  const [vaultAddon, setVaultAddon] = useState(false);
  const [missionOpen, setMissionOpen] = useState(false);
  const missionRef = useRef(null);
  const [arrivingOpen, setArrivingOpen] = useState(false);
  const arrivingRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  // Preview-only gate — keeps live production showing "Arriving Soon" while
  // allowing internal testers to actually toggle the Memory Vault add-on.
  const previewUnlocked = isPreviewEnv();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (missionRef.current && !missionRef.current.contains(e.target)) {
        setMissionOpen(false);
      }
    };
    if (missionOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [missionOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (arrivingRef.current && !arrivingRef.current.contains(e.target)) {
        setArrivingOpen(false);
      }
    };
    if (arrivingOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [arrivingOpen]);

  // Close the mobile hamburger menu on outside-tap / route change.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [mobileMenuOpen]);

  const scrollToCustomise = () => {
    document.getElementById('customise')?.scrollIntoView({ behavior: 'smooth' });
  };

  const buildOptions = () => [
    colourChoice && `Colour: ${colourChoice}`,
    `Message: ${msgChoice}`,
    premiumPackaging && 'Premium Packaging Upgrade (+£9.99)',
    vaultAddon && 'Memory Vault Extension (+£29.99)',
  ].filter(Boolean);

  const totalPrice = 64.99 + (premiumPackaging ? 9.99 : 0) + (vaultAddon ? 29.99 : 0);

  const handleBuyNow = () => {
    addItem({ id: premiumPackaging ? 'letters-that-last-premium' : 'letters-that-last', name: premiumPackaging ? 'Letters That Last (Premium Packaging)' : 'Letters That Last', collection: 'The Timeless Collection', price: premiumPackaging ? 74.98 : 64.99, image: IMAGES.product, options: buildOptions() });
    if (vaultAddon) {
      addItem({ id: 'memory-vault-addon', name: 'Sent2ment Memory Vault (Add-On)', collection: 'Memory Vault', price: 29.99 });
    }
    setTimeout(() => {
      navigate('/letters-that-last/checkout?source=ltl');
    }, 100);
  };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", background: C.bg, color: C.text }} className="min-h-screen">

      {/* ── Navigation ── */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: C.cream, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between h-[72px] sm:h-24">
          <Link to="/" aria-label="Sent2ment home" className="flex items-center gap-3 sm:gap-4 min-w-0" data-testid="ltl-logo">
            <img
              src="/sent2ment-logo.png"
              alt="Sent2ment — Preserving What Matters Most"
              className="h-16 sm:h-24 w-auto object-contain"
              data-testid="ltl-logo-img"
            />
            <span
              className="hidden xl:flex flex-col leading-tight text-[11px] xl:text-xs tracking-[0.22em] uppercase font-medium"
              style={{ color: C.gold }}
              data-testid="ltl-nav-wordmark"
            >
              <span className="whitespace-nowrap">Spoken Echoes™</span>
              <span className="mt-0.5 whitespace-nowrap">Timeless Collection</span>
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5 lg:gap-6 shrink-0">
            <Link
              to="/"
              data-testid="ltl-nav-home"
              className="hidden md:inline whitespace-nowrap text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
              style={{ color: C.gold }}
            >
              Home
            </Link>
            <div className="relative" ref={missionRef}>
              <button
                type="button"
                onClick={() => setMissionOpen(v => !v)}
                data-testid="ltl-mission-trigger"
                aria-expanded={missionOpen}
                className="hidden md:flex items-center gap-1.5 whitespace-nowrap text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
                style={{ color: C.gold }}
              >
                Our Story
                <ChevronDown size={14} strokeWidth={1.6} style={{ transform: missionOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 300ms ease' }} />
              </button>
              <AnimatePresence>
                {missionOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed top-[72px] left-4 right-4 w-auto max-h-[calc(100vh-8rem)] overflow-y-auto sm:absolute sm:top-full sm:left-auto sm:right-0 sm:mt-3 sm:w-[min(92vw,520px)] sm:max-w-[520px] sm:max-h-[calc(100vh-9rem)] sm:overflow-y-auto z-50 p-6 sm:p-7"
                    style={{ background: C.midnight, border: `1px solid ${C.gold}40`, boxShadow: '0 20px 60px rgba(6,16,23,0.35)', borderRadius: '4px' }}
                    data-testid="ltl-mission-panel"
                  >
                    <p className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-3" style={{ color: C.gold }}>Our Story</p>
                    <p className="text-base sm:text-lg italic font-light leading-snug mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.cream }}>
                      Inspired &amp; passionate about helping people heal, grow, and feel held.
                    </p>
                    <p className="text-sm leading-relaxed mb-4" style={{ color: `${C.cream}CC` }}>
                      Sent2ment exists to support people from every background and walk of life — caring for the whole of our community, with no one left out.
                    </p>
                    <p className="text-sm leading-relaxed mb-4" style={{ color: `${C.cream}CC` }}>
                      From helping young people shape their aspirations and dreams, to gifting families a much-needed holiday they've never been able to offer their children, to anything in between — we want to walk alongside people, one person at a time, with kindness and real care.
                    </p>
                    <p className="text-sm leading-relaxed mb-5" style={{ color: `${C.cream}CC` }}>
                      A portion of every profit we make will fund a new chapter of giving — <span className="italic" style={{ color: C.gold }}>Sent2ment's Initiative</span> — so that every keepsake you create quietly helps someone else find theirs.
                    </p>
                    <p className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: C.gold }} data-testid="ltl-mission-signoff">Sent2ment's Initiative · coming soon</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {SHOW_FUTURE_PRODUCTS && (
            <div className="relative" ref={arrivingRef}>
              <button
                type="button"
                onClick={() => setArrivingOpen(v => !v)}
                data-testid="ltl-arriving-trigger"
                aria-expanded={arrivingOpen}
                className="hidden md:flex items-center gap-1.5 whitespace-nowrap text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
                style={{ color: C.gold }}
              >
                Arriving Soon
                <ChevronDown size={14} strokeWidth={1.6} style={{ transform: arrivingOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 300ms ease' }} />
              </button>
              <AnimatePresence>
                {arrivingOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed top-[72px] left-4 right-4 w-auto max-h-[calc(100vh-8rem)] overflow-y-auto sm:absolute sm:top-full sm:left-auto sm:right-0 sm:mt-3 sm:w-[88vw] sm:max-w-[440px] z-50 p-6 sm:p-7"
                    style={{ background: C.midnight, border: `1px solid ${C.gold}40`, boxShadow: '0 20px 60px rgba(6,16,23,0.35)', borderRadius: '4px' }}
                    data-testid="ltl-arriving-panel"
                  >
                    <p className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-3" style={{ color: C.gold }}>Arriving Soon</p>
                    <p className="text-sm leading-relaxed" style={{ color: `${C.cream}CC` }}>
                      We&rsquo;re quietly working in the background on more ways to hold what matters &mdash; including pre-teen journals, Pocket Keepsakes&trade; to carry with you and hear anytime, and children&rsquo;s story books with comforting characters you can read together, even when you&rsquo;re not there.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            )}
            {SHOW_FUTURE_PRODUCTS && (
            <a
              href="/memory-vault"
              data-testid="ltl-nav-memory-vault"
              className="hidden md:inline whitespace-nowrap text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
              style={{ color: C.gold }}
            >
              Memory Vault
            </a>
            )}
            <a
              href="/my-orders"
              data-testid="ltl-nav-my-orders"
              className="hidden md:inline whitespace-nowrap text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
              style={{ color: C.gold }}
            >
              My Orders
            </a>
            <button onClick={() => setIsOpen(true)} className="relative transition-colors duration-300" style={{ color: C.darkText }} data-testid="ltl-cart-btn">
              <ShoppingCart strokeWidth={1.2} size={22} />
              {totalItems > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-[9px] font-semibold flex items-center justify-center rounded-full min-w-[18px] min-h-[18px] leading-none" style={{ background: C.gold, color: C.cream }}>{totalItems}</span>
              )}
            </button>
            {/* Mobile hamburger — hidden on md+ */}
            <div className="md:hidden relative" ref={mobileMenuRef}>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(v => !v)}
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                data-testid="ltl-nav-hamburger"
                className="flex items-center justify-center transition-opacity duration-300 hover:opacity-80"
                style={{ color: C.gold, width: 32, height: 32 }}
              >
                {mobileMenuOpen ? <X strokeWidth={1.5} size={22} /> : <Menu strokeWidth={1.5} size={22} />}
              </button>
              <AnimatePresence>
                {mobileMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed top-[72px] left-4 right-4 z-50 p-5"
                    style={{ background: C.cream, border: `1px solid ${C.gold}33`, boxShadow: '0 18px 52px rgba(11,31,58,0.12)', borderRadius: '6px' }}
                    data-testid="ltl-mobile-menu"
                    role="menu"
                  >
                    <button
                      type="button"
                      onClick={() => { setMobileMenuOpen(false); setTimeout(() => setMissionOpen(true), 50); }}
                      data-testid="ltl-mobile-menu-mission"
                      className="w-full text-left py-3 text-[13px] tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
                      style={{ color: C.gold, borderBottom: `1px solid ${C.gold}20` }}
                    >
                      Our Story
                    </button>
                    {SHOW_FUTURE_PRODUCTS && (
                    <button
                      type="button"
                      onClick={() => { setMobileMenuOpen(false); setTimeout(() => setArrivingOpen(true), 50); }}
                      data-testid="ltl-mobile-menu-arriving"
                      className="w-full text-left py-3 text-[13px] tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
                      style={{ color: C.gold, borderBottom: `1px solid ${C.gold}20` }}
                    >
                      Arriving Soon
                    </button>
                    )}
                    {SHOW_FUTURE_PRODUCTS && (
                    <a
                      href="/memory-vault"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="ltl-mobile-menu-memory-vault"
                      className="block w-full py-3 text-[13px] tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
                      style={{ color: C.gold, borderBottom: `1px solid ${C.gold}20` }}
                    >
                      Memory Vault
                    </a>
                    )}
                    <a
                      href="/my-orders"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="ltl-mobile-menu-my-orders"
                      className="block w-full py-3 text-[13px] tracking-[0.22em] uppercase font-medium transition-opacity duration-300 hover:opacity-80"
                      style={{ color: C.gold }}
                    >
                      My Orders
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative min-h-screen">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11, 31, 58, 0.55), rgba(11, 31, 58, 0.78))' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(11, 31, 58, 0.2) 0%, rgba(11, 31, 58, 0.6) 65%, rgba(11, 31, 58, 0.82) 100%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-10 sm:pt-12 md:pt-16 pb-24 md:pb-32 w-full">
          <div className="max-w-3xl mx-auto text-center">
            <motion.p {...stagger(0.05)} className="text-[11px] sm:text-xs tracking-[0.34em] uppercase font-semibold mb-8" style={{ color: C.gold, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }} data-testid="ltl-hero-eyebrow">
              The Timeless Collection
            </motion.p>
            <motion.h1
              {...stagger(0.1)}
              className="text-4xl sm:text-6xl md:text-7xl font-light tracking-[0.02em] mb-10"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold, textShadow: '0 2px 14px rgba(0,0,0,0.5)' }}
              data-testid="ltl-hero-heading"
            >
              Letters That Last<sup style={{ fontSize: '0.32em', verticalAlign: 'super', marginLeft: '0.15em', letterSpacing: '0' }}>&trade;</sup>
            </motion.h1>
            <motion.div
              {...stagger(0.2)}
              className="mb-10"
              data-testid="ltl-hero-signature-lines"
            >
              <p className="text-lg sm:text-xl md:text-2xl italic leading-[1.7] tracking-[0.02em]" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.cream, opacity: 0.94, textShadow: '0 1px 6px rgba(0,0,0,0.45)' }}>
                Written with intention.
                <br />
                Spoken from the heart.
                <br />
                Treasured for a lifetime.
              </p>
            </motion.div>
            <motion.p {...stagger(0.32)} className="text-base md:text-lg leading-loose mb-12 max-w-xl mx-auto tracking-[0.02em]" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.cream, opacity: 0.88, textShadow: '0 1px 6px rgba(0,0,0,0.4)' }} data-testid="ltl-hero-paragraph">
              A beautifully crafted letter, bringing together your written words and the sound of your voice &mdash; a keepsake to return to again and again, keeping the connection close through the comfort of your familiar voice.
            </motion.p>
            <motion.div
              {...stagger(0.4)}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8"
              data-testid="ltl-hero-actions"
            >
              <a
                href="#customise"
                onClick={(e) => { e.preventDefault(); scrollToCustomise(); }}
                data-testid="ltl-hero-cta"
                className="inline-block font-semibold tracking-[0.24em] uppercase text-xs sm:text-sm px-12 py-4 rounded transition-all duration-500 hover:shadow-[0_6px_28px_rgba(198,169,107,0.45)]"
                style={{ background: C.gold, color: C.btnText, boxShadow: '0 4px 20px rgba(198,169,107,0.28)' }}
              >
                Create Yours
              </a>
              <a
                href="#how-it-works"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector('[data-testid="ltl-how-heading"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                data-testid="ltl-hero-secondary"
                className="inline-flex items-center text-[11px] sm:text-xs tracking-[0.22em] uppercase font-light transition-opacity duration-300 hover:opacity-100"
                style={{ color: `${C.cream}B3`, opacity: 0.78 }}
              >
                Discover How It Works
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Brand Introduction (immediately beneath the hero) ── */}
      <section className="py-20 md:py-28" style={{ background: C.cream }} data-testid="ltl-intro">
        <div className="max-w-xl mx-auto px-6 md:px-12 text-center">
          {/* "For Every Chapter." eyebrow removed — the heading below is now the standalone statement. */}
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-light leading-[1.2] tracking-[0.005em] mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
            data-testid="ltl-intro-heading"
          >
            The words that matter most deserve to last.
          </h2>
          <p
            className="text-base sm:text-lg leading-relaxed mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec, maxWidth: '30em' }}
            data-testid="ltl-intro-body"
          >
            Letters That Last<sup style={{ fontSize: '0.55em', verticalAlign: 'super' }}>&trade;</sup> brings together the permanence of the written word and the comfort of a familiar voice — beautifully preserved in one deeply personal keepsake.
          </p>
        </div>
      </section>

      {/* ── Universal emotional reminder — Letters That Last™ is not only for goodbye.
          Compact section restored for the shortened launch homepage. */}
      <section className="pb-16 md:pb-24" style={{ background: C.cream }} data-testid="ltl-universal-moment">
        <div className="max-w-xl mx-auto px-6 md:px-12 text-center">
          <motion.p
            {...fadeUp}
            className="text-lg sm:text-xl md:text-2xl italic leading-loose"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
            data-testid="ltl-universal-moment-quote"
          >
            This is not only for goodbye.
            <br />
            It is for &ldquo;I&rsquo;m here.&rdquo;
            <br />
            &ldquo;I&rsquo;m proud of you.&rdquo;
            <br />
            &ldquo;I love you.&rdquo;
          </motion.p>
          <motion.p
            {...stagger(0.15)}
            className="mt-8 text-sm sm:text-base leading-relaxed mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec, maxWidth: '32em' }}
            data-testid="ltl-universal-moment-body"
          >
            For birthdays, anniversaries, distance, new beginnings &mdash; and the words worth hearing again.
          </motion.p>
        </div>
      </section>

      {/* ── Product Showcase ── */}
      <section className="py-14 md:py-20" style={{ background: C.cream }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeUp}>
              <p
                className="text-3xl sm:text-4xl md:text-5xl font-light italic tracking-[0.04em] text-center mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}
                data-testid="ltl-product-images-heading"
              >
                Letters That Last<sup style={{ fontSize: '0.32em', verticalAlign: 'super', marginLeft: '0.15em', letterSpacing: '0' }}>&trade;</sup>
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className="relative overflow-hidden"
                  style={{
                    borderRadius: '18px',
                    border: `1px solid ${C.gold}40`,
                    boxShadow: '0 10px 36px rgba(11, 31, 58, 0.18), 0 2px 10px rgba(198, 169, 107, 0.18)',
                    background: 'radial-gradient(120% 90% at 20% 0%, rgba(198,169,107,0.32) 0%, rgba(245,241,232,0.55) 45%, rgba(11,31,58,0.92) 100%)',
                    padding: '14px',
                  }}
                >
                  <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ background: 'radial-gradient(60% 50% at 80% 100%, rgba(198,169,107,0.22), transparent 70%)' }} />
                  <img src="/ltl-pack-a.png" alt="Sent2ment Letters That Last keepsake — Midnight Navy edition" className="relative w-full aspect-[4/3] object-cover" style={{ borderRadius: '12px', boxShadow: '0 6px 18px rgba(0,0,0,0.22)' }} data-testid="ltl-product-image-a" />
                </div>
                <div
                  className="relative overflow-hidden"
                  style={{
                    borderRadius: '18px',
                    border: `1px solid ${C.gold}40`,
                    boxShadow: '0 10px 36px rgba(11, 31, 58, 0.18), 0 2px 10px rgba(198, 169, 107, 0.18)',
                    background: 'radial-gradient(120% 90% at 20% 0%, rgba(198,169,107,0.32) 0%, rgba(245,241,232,0.55) 45%, rgba(11,31,58,0.92) 100%)',
                    padding: '14px',
                  }}
                >
                  <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ background: 'radial-gradient(60% 50% at 80% 100%, rgba(198,169,107,0.22), transparent 70%)' }} />
                  <img src="/ltl-pack-b.png" alt="Sent2ment Letters That Last keepsake — Cream edition" className="relative w-full aspect-[4/3] object-cover" style={{ borderRadius: '12px', boxShadow: '0 6px 18px rgba(0,0,0,0.22)' }} data-testid="ltl-product-image-b" />
                </div>
              </div>
              <p
                className="text-[10px] sm:text-[11px] italic text-center mt-4 tracking-[0.02em]"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec, opacity: 0.65 }}
                data-testid="ltl-product-images-note"
              >
                Product imagery is illustrative while final photography is being completed.
              </p>
            </motion.div>
            <motion.div {...stagger(0.15)}>
              <p className="text-xs tracking-[0.2em] uppercase mb-4 font-medium" style={{ color: C.gold }}>The Keepsake</p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }} className="text-2xl sm:text-3xl font-light leading-snug mb-8">Beautifully considered. Thoughtfully preserved.</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-10" data-testid="ltl-keepsake-grid">
                {[
                  {
                    icon: PenLine,
                    label: 'Your Letter',
                    body: 'In your chosen colour, personalised with words selected or written by you.',
                  },
                  {
                    icon: Volume2,
                    label: (
                      <>
                        Spoken Echoes<sup style={{ fontSize: '0.55em', verticalAlign: 'super', marginLeft: '0.1em' }}>&trade;</sup>
                      </>
                    ),
                    body: 'A private voice message, accessed through the QR code inside your keepsake.',
                  },
                  {
                    icon: Package,
                    label: 'Created To Give',
                    body: 'Carefully presented and ready to become part of someone\u2019s story.',
                  },
                  {
                    icon: Shield,
                    label: 'Preserved For The Future',
                    body: 'Securely held and available through your keepsake whenever you return to it.',
                  },
                ].map((it) => (
                  <div key={typeof it.label === 'string' ? it.label : 'spoken-echoes'} className="flex flex-col">
                    <it.icon size={20} strokeWidth={1.2} style={{ color: C.gold }} className="mb-3" aria-hidden="true" />
                    <p className="text-[11px] tracking-[0.22em] uppercase font-medium mb-2" style={{ color: C.darkText }}>
                      {it.label}
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: C.darkTextSec, fontFamily: "'Cormorant Garamond', serif" }}>
                      {it.body}
                    </p>
                  </div>
                ))}
              </div>
              {/* ── QR Keepsake Card — small inline refinement inside the product-details
                  area, between the keepsake grid and the price. No new large section. */}
              <div
                className="mt-2 mb-10 pl-5"
                style={{ borderLeft: `1px solid ${C.gold}40` }}
                data-testid="ltl-qr-card"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Scan size={18} strokeWidth={1.3} style={{ color: C.gold }} aria-hidden="true" />
                  <p
                    className="text-base sm:text-lg italic leading-snug"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
                    data-testid="ltl-qr-card-heading"
                  >
                    A letter to treasure. A voice to carry with you.
                  </p>
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec, maxWidth: '32em' }}
                  data-testid="ltl-qr-card-body"
                >
                  Your keepsake includes a separate QR card, giving private access to your voice message wherever they choose to keep it.
                </p>
              </div>
              <p className="text-3xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }} data-testid="ltl-price">£64.99</p>
              <p className="text-xs mb-2" style={{ color: C.darkTextSec }} data-testid="ltl-delivery-note">Standard UK delivery included.</p>
              <p className="text-xs italic mb-5 leading-relaxed" style={{ color: C.gold, fontFamily: "'Cormorant Garamond', serif", maxWidth: '22em' }} data-testid="ltl-price-supporting-line">Created with care for one person, one story and one lasting moment.</p>
              <a
                href="#customise"
                onClick={(e) => { e.preventDefault(); scrollToCustomise(); }}
                data-testid="ltl-personalise-link"
                className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-medium pb-1 transition-opacity duration-300 hover:opacity-80"
                style={{ color: C.gold, borderBottom: `1px solid ${C.gold}55`, fontFamily: 'inherit' }}
              >
                <span>Personalise yours</span>
                <span aria-hidden="true">↓</span>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── For Every Chapter — hidden for the shortened Letters That Last™ launch homepage.
          Section code preserved below behind a false flag so it can be restored later. */}
      {false && (
      <section className="py-24 md:py-32" style={{ background: C.cream }} data-testid="ltl-chapters">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p
            className="text-[10px] sm:text-[11px] tracking-[0.34em] uppercase font-semibold mb-6"
            style={{ color: C.gold }}
            data-testid="ltl-chapters-eyebrow"
          >
            For Every Chapter.
          </p>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-light leading-[1.2] tracking-[0.005em] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
            data-testid="ltl-chapters-heading"
          >
            Created for the moments that stay with us.
          </h2>
          <p
            className="text-base sm:text-lg leading-relaxed mx-auto mb-20 md:mb-24"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec, maxWidth: '34em' }}
            data-testid="ltl-chapters-intro"
          >
            For celebrations, time apart, words left unsaid and love that deserves to be preserved.
          </p>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-14 md:gap-x-14 md:gap-y-16 text-left max-w-4xl mx-auto"
            data-testid="ltl-chapters-grid"
          >
            {[
              { label: 'Love', body: 'For the words that belong between two people.' },
              { label: 'Family', body: 'For voices, stories and reassurance to return to.' },
              { label: 'Milestones', body: 'For birthdays, weddings, new beginnings and proud moments.' },
              { label: 'Time Apart', body: 'For distance, deployment, care and circumstances that keep people apart.' },
              { label: 'Comfort', body: 'For the words someone may need to hear more than once.' },
              { label: 'Remembrance', body: 'For preserving a voice and presence beyond a single moment.' },
            ].map((c) => (
              <div key={c.label} className="max-w-xs">
                <p
                  className="text-[11px] tracking-[0.28em] uppercase font-medium mb-4"
                  style={{ color: C.gold }}
                >
                  {c.label}
                </p>
                <p
                  className="text-base leading-relaxed"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec }}
                >
                  {c.body}
                </p>
              </div>
            ))}
          </div>

          <div
            className="mt-24 md:mt-28 mx-auto max-w-md"
            data-testid="ltl-chapters-closing"
          >
            <p
              className="text-lg sm:text-xl italic leading-loose"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
            >
              This is not only for goodbye.
              <br />
              It is for &ldquo;I&rsquo;m here.&rdquo;
              <br />
              &ldquo;I&rsquo;m proud of you.&rdquo;
              <br />
              &ldquo;I love you.&rdquo;
              <br />
              And every word that deserves to stay.
            </p>
          </div>
        </div>
      </section>
      )}

      {/* ── Customise Your Letter ── */}
      <section id="customise" className="pt-10 md:pt-14 pb-10 md:pb-14" style={{ background: C.cream }}>
        <div className="max-w-xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }} className="text-4xl sm:text-5xl font-light tracking-tight" data-testid="ltl-customise-heading">Create Your Keepsake</h2>
          </motion.div>

          {/* Step 1: Colour */}
          <motion.div {...stagger(0.1)} className="mb-12">
            <p className="text-xs tracking-[0.2em] uppercase mb-5 font-medium" style={{ color: C.gold }}>Step 1 — Choose Your Colour</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => setColourChoice('Midnight Blue')} data-testid="ltl-colour-midnight"
                className="relative border p-5 text-left transition-all duration-300"
                style={{
                  background: C.midnight,
                  borderColor: colourChoice === 'Midnight Blue' ? C.gold : `${C.gold}20`,
                  boxShadow: colourChoice === 'Midnight Blue' ? `0 0 20px ${C.goldMuted}` : 'none',
                }}>
                <p className="text-sm font-medium tracking-wide mb-1" style={{ color: C.gold }}>Midnight Blue</p>
                <p className="text-xs" style={{ color: `${C.textSec}80` }}>Champagne-gold detail on deep midnight navy.</p>
                {colourChoice === 'Midnight Blue' && <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: C.gold }}><svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke={C.bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
              </button>
              <button onClick={() => setColourChoice('Cream')} data-testid="ltl-colour-cream"
                className="relative border p-5 text-left transition-all duration-300"
                style={{
                  background: '#FFFDF8',
                  borderColor: colourChoice === 'Cream' ? C.gold : `${C.gold}25`,
                  boxShadow: colourChoice === 'Cream' ? `0 0 20px ${C.goldMuted}` : 'none',
                }}>
                <p className="text-sm font-medium tracking-wide mb-1" style={{ color: C.darkText }}>Cream</p>
                <p className="text-xs" style={{ color: C.darkTextSec }}>Champagne-gold detail on warm cream.</p>
                {colourChoice === 'Cream' && <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: C.gold }}><svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke={C.bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
              </button>
            </div>
          </motion.div>

          {/* Step 2: Words */}
          <motion.div {...stagger(0.2)} className="mb-12">
            <p className="text-xs tracking-[0.2em] uppercase mb-2 font-medium" style={{ color: C.gold }}>Step 2 — Choose Your Words</p>
            <p className="text-sm italic mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec }} data-testid="ltl-step2-supporting">
              Select one of our thoughtfully written messages or personalise your own.
            </p>
            <MessagePreview productType="letters" productId="ltl-landing" showAnimation={false} onSelect={setMsgChoice} colourChoice={colourChoice} />
          </motion.div>

          {/* Step 3: Presentation */}
          <motion.div {...stagger(0.25)} className="mb-12">
            <p className="text-xs tracking-[0.2em] uppercase mb-5 font-medium" style={{ color: C.gold }}>Step 3 — Presentation</p>
            <div className={`grid grid-cols-1 ${SHOW_PREMIUM_PACKAGING ? 'sm:grid-cols-2' : ''} gap-3`}>
              <button
                onClick={() => setPremiumPackaging(false)}
                data-testid="ltl-packaging-standard"
                className="relative border p-5 text-left transition-all duration-300"
                style={{
                  background: '#FFFDF8',
                  borderColor: !premiumPackaging ? C.gold : `${C.gold}25`,
                  boxShadow: !premiumPackaging ? `0 0 20px ${C.goldMuted}` : 'none',
                }}
              >
                <p className="text-sm font-medium tracking-wide mb-1" style={{ color: C.darkText }}>Signature Presentation</p>
                <p className="text-xs" style={{ color: C.darkTextSec }}>Included — beautifully presented and ready to give.</p>
                {!premiumPackaging && <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: C.gold }}><svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke={C.bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
              </button>
              {SHOW_PREMIUM_PACKAGING && (
                <button
                  onClick={() => setPremiumPackaging(true)}
                  data-testid="ltl-packaging-premium"
                  className="relative border p-5 text-left transition-all duration-300"
                  style={{
                    background: C.midnight,
                    borderColor: premiumPackaging ? C.gold : `${C.gold}20`,
                    boxShadow: premiumPackaging ? `0 0 20px ${C.goldMuted}` : 'none',
                  }}
                >
                  <p className="text-sm font-medium tracking-wide mb-1" style={{ color: C.gold }}>Premium Packaging Upgrade</p>
                  <p className="text-xs" style={{ color: `${C.textSec}80` }}>+£9.99 — elevated unboxing keepsake</p>
                  {premiumPackaging && <div className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: C.gold }}><svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke={C.bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
                </button>
              )}
            </div>
          </motion.div>

          {/* Memory Vault Add-On — feature-flagged off (SHOW_VAULT_ADDON=false).
              State, Stripe product id, backend PRODUCTS entry, DB fields and
              vault routes all preserved. Standalone /memory-vault is unaffected. */}
          {SHOW_VAULT_ADDON && (
          <motion.div
            {...stagger(0.25)}
            className="mb-8"
            data-testid="ltl-vault-addon-block"
          >
            <button
              type="button"
              onClick={previewUnlocked ? () => setVaultAddon(v => !v) : (e) => e.preventDefault()}
              disabled={!previewUnlocked}
              data-testid="ltl-vault-addon-toggle"
              aria-pressed={previewUnlocked ? vaultAddon : false}
              aria-disabled={previewUnlocked ? 'false' : 'true'}
              className={`w-full text-left p-5 sm:p-6 transition-all duration-300 ${previewUnlocked ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              style={{
                background: '#FFFFFF',
                border: `1px solid ${previewUnlocked && vaultAddon ? C.gold : `${C.gold}25`}`,
                boxShadow: previewUnlocked && vaultAddon ? `0 0 20px ${C.goldMuted}` : 'none',
                borderRadius: '4px',
                opacity: previewUnlocked ? 1 : 0.85,
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-300"
                  style={{
                    background: previewUnlocked && vaultAddon ? C.gold : 'transparent',
                    border: `1px solid ${previewUnlocked && vaultAddon ? C.gold : `${C.gold}55`}`,
                  }}
                  aria-hidden
                >
                  {previewUnlocked && vaultAddon && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke={C.bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-2"
                    style={{ color: C.gold }}
                    data-testid="ltl-vault-addon-overline"
                  >
                    {previewUnlocked ? 'Memory Vault\u2122 · Optional Add-On' : 'Memory Vault\u2122 · Arriving Soon'}
                  </p>
                  <p
                    className="text-lg sm:text-xl font-light leading-snug mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
                    data-testid="ltl-vault-addon-title"
                  >
                    Extend their Sent2ment experience — <span style={{ color: C.gold }}>£29.99</span>
                  </p>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: C.darkTextSec }}
                    data-testid="ltl-vault-addon-desc"
                  >
                    A private space they can return to — with your voice, hidden messages, affirmation cards and moments they'll discover over time.
                  </p>
                  <p
                    className="text-[11px] italic mt-3"
                    style={{ color: `${C.darkTextSec}CC`, fontFamily: "'Cormorant Garamond', serif" }}
                    data-testid="ltl-vault-addon-note"
                  >
                    {previewUnlocked
                      ? 'Internal preview · test access enabled. Toggle to add to your keepsake.'
                      : 'Available shortly — the Memory Vault\u2122 is launching soon as part of your Letters That Last\u2122 keepsake.'}
                  </p>
                </div>
              </div>
            </button>
            <p
              className="text-xs text-center mt-4"
              style={{ color: C.darkTextSec, fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="ltl-vault-digital-link-wrap"
            >
              <a
                href="/memory-vault"
                className="italic underline-offset-4 hover:underline transition-colors duration-300"
                style={{ color: C.gold }}
                data-testid="ltl-vault-digital-link"
              >
                Prefer something digital only? Create a private Memory Vault&trade; instead &rarr;
              </a>
            </p>
          </motion.div>
          )}

          {/* Create Your Keepsake */}
          <motion.div {...stagger(0.3)} className="border-t pt-8" style={{ borderColor: `${C.gold}20` }}>
            {!colourChoice && (
              <p
                className="text-sm italic text-center mb-5"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec }}
                data-testid="ltl-colour-hint"
              >
                Please choose your letter colour to continue.
              </p>
            )}
            {colourChoice && (
              <p className="text-xs text-center mb-4" style={{ color: C.darkTextSec }} data-testid="ltl-total-price">
                Total: <span className="font-semibold" style={{ color: C.darkText }}>£{totalPrice.toFixed(2)}</span>
                {SHOW_PREMIUM_PACKAGING && premiumPackaging && <span className="italic ml-1" style={{ color: C.gold }}>· includes Premium Packaging</span>}
                {SHOW_VAULT_ADDON && vaultAddon && <span className="italic ml-1" style={{ color: C.gold }}>· includes Memory Vault&trade;</span>}
              </p>
            )}
            <button
              onClick={() => { if (colourChoice) handleBuyNow(); }}
              disabled={!colourChoice}
              aria-disabled={!colourChoice}
              data-testid="ltl-customise-buy"
              className={`w-full font-medium tracking-wide uppercase text-sm px-8 py-4 transition-all duration-300 ${colourChoice ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              style={{ background: colourChoice ? C.gold : `${C.gold}40`, color: colourChoice ? C.btnText : `${C.btnText}80`, boxShadow: colourChoice ? '0 4px 20px rgba(198,169,107,0.25)' : 'none', borderRadius: '0.25rem' }}>
              Create Your Keepsake
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── How It Works (now also carries the trust-strip) ── */}
      <section className="py-14 md:py-20" style={{ background: C.cream }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase mb-4 font-medium" style={{ color: C.gold }}>How It Works</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }} className="text-4xl sm:text-5xl font-light tracking-tight" data-testid="ltl-how-heading">Three simple steps.</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              { icon: PenLine, num: '01', title: 'Make it yours', desc: 'Choose your colour, select a thoughtfully written message or create words of your own.' },
              { icon: Package, num: '02', title: 'We craft your letter', desc: (<>Your keepsake is carefully printed, finished and paired with its unique Spoken Echoes<sup style={{ fontSize: '0.55em', verticalAlign: 'super', marginLeft: '0.1em' }}>&trade;</sup> access.</>) },
              { icon: Volume2, num: '03', title: 'Add your voice', desc: 'After purchase, record in your own time whenever you feel ready. Your private voice message will be preserved securely.' },
            ].map((step, i) => (
              <motion.div key={step.num} {...stagger(i * 0.12)} className="text-center py-[26px] px-6 sm:p-8 border" style={{ background: '#FFFDF8', borderColor: `${C.gold}25`, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                <step.icon className="mx-auto mb-5" size={32} strokeWidth={1.2} style={{ color: C.gold }} />
                <p className="text-3xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.goldRich }}>{step.num}</p>
                <h3 className="text-sm font-medium tracking-wide uppercase mb-3" style={{ color: C.darkText }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.darkTextSec }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Trust strip — merged in to keep one rhythm instead of two */}
          <motion.div {...fadeUp} className="mt-16 pt-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center" style={{ borderTop: `1px solid ${C.gold}25` }} data-testid="ltl-trust-strip">
            {[
              { icon: Mic, label: 'Your Voice', sub: 'Record privately, in your own time.' },
              { icon: Scan, label: 'Private Access', sub: (<>Held behind your unique Spoken Echoes<sup style={{ fontSize: '0.65em', verticalAlign: 'super', marginLeft: '0.05em' }}>&trade;</sup> QR code.</>) },
              { icon: Shield, label: 'Securely Preserved', sub: 'Held safely and made available through your keepsake.' },
              { icon: Heart, label: 'Crafted With Care', sub: 'Created individually, never impersonally.' },
            ].map((item) => (
              <div key={item.label}>
                <item.icon className="mx-auto mb-3" size={22} strokeWidth={1.2} style={{ color: C.gold }} />
                <p className="text-xs font-medium tracking-wide uppercase mb-1" style={{ color: C.darkText }}>{item.label}</p>
                <p className="text-xs leading-relaxed" style={{ color: C.darkTextSec }}>{item.sub}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Manifesto "Some words should never be lost" — hidden for the shortened launch
          homepage. Code preserved behind a false flag so it can be restored later. */}
      {false && (
      <section className="py-16 md:py-24" style={{ background: C.cream }} data-testid="ltl-manifesto">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            {...fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-light leading-[1.25] tracking-[0.005em] mb-10"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
            data-testid="ltl-manifesto-heading"
          >
            Some words should never be lost.
          </motion.h2>
          <motion.div
            {...stagger(0.15)}
            className="w-10 h-px mx-auto mb-10"
            style={{ background: `${C.gold}55` }}
            aria-hidden="true"
          />
          <motion.p
            {...stagger(0.2)}
            className="text-base md:text-lg leading-relaxed mb-5 mx-auto"
            style={{ color: C.darkTextSec, maxWidth: '36em' }}
            data-testid="ltl-manifesto-body-1"
          >
            Life does not always give us the perfect moment to say everything we feel.
          </motion.p>
          <motion.p
            {...stagger(0.28)}
            className="text-base md:text-lg leading-relaxed mb-10 mx-auto"
            style={{ color: C.darkTextSec, maxWidth: '36em' }}
            data-testid="ltl-manifesto-body-2"
          >
            Letters That Last<sup style={{ fontSize: '0.55em', verticalAlign: 'super', marginLeft: '0.05em' }}>&trade;</sup> preserves your words and your voice, creating something deeply personal to hold now and return to in the years ahead.
          </motion.p>
          <motion.p
            {...stagger(0.35)}
            className="text-lg sm:text-xl italic leading-relaxed mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold, maxWidth: '32em' }}
            data-testid="ltl-manifesto-closing"
          >
            Where words and voices become keepsakes.
          </motion.p>
        </div>
      </section>
      )}

      {/* ── Introducing the Memory Vault (digital) — calm rounded card ── */}
      {SHOW_FUTURE_PRODUCTS && (
      <section className="relative py-20 md:py-28 mx-4 sm:mx-6 md:mx-10 rounded-[2.5rem] sm:rounded-[3rem] overflow-hidden" data-testid="ltl-memory-vault-intro" style={{ boxShadow: 'inset 0 0 0 1px rgba(198, 169, 107, 0.20), 0 12px 40px rgba(11,31,58,0.18)' }}>
        <div className="absolute inset-0">
          <img src={IMAGES.texture} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(6,16,23,0.92), rgba(6,16,23,0.84))' }} />
        </div>
        <div className="relative max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="text-[11px] sm:text-xs tracking-[0.34em] uppercase font-semibold mb-5" style={{ color: C.gold }} data-testid="ltl-vault-overline">
              Memory Vault<sup style={{ fontSize: '0.7em', verticalAlign: 'super', marginLeft: '0.1em', letterSpacing: '0' }}>&trade;</sup>
            </p>
            <p className="text-3xl sm:text-4xl md:text-5xl font-light leading-[1.25] mb-8 tracking-[0.01em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', color: C.gold }} data-testid="ltl-vault-tagline">
              A private place to return to.
            </p>
            <p className="text-base leading-relaxed mb-6 max-w-md mx-auto" style={{ color: `${C.textSec}` }} data-testid="ltl-vault-paragraph">
              For the words, voices, photographs and moments you&rsquo;d like someone to return to&mdash;especially when distance or circumstance makes a physical keepsake difficult.
            </p>
            <p className="text-sm italic leading-relaxed mb-10 max-w-md mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", color: `${C.gold}CC` }} data-testid="ltl-vault-italic">
              Shared privately by invitation. No post required.
            </p>
            <a
              href="/memory-vault"
              data-testid="ltl-vault-cta"
              className="inline-block font-semibold tracking-[0.2em] uppercase text-xs sm:text-sm px-14 py-5 rounded transition-all duration-500 hover:shadow-[0_6px_28px_rgba(198,169,107,0.35)]"
              style={{ background: C.gold, color: C.btnText, boxShadow: '0 4px 20px rgba(198,169,107,0.25)' }}
            >
              Explore Memory Vault
            </a>
            <p
              className="text-xs italic mt-8 leading-relaxed mx-auto"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: `${C.textSec}80`, maxWidth: '26em' }}
              data-testid="ltl-vault-note-secondary"
            >
              Ideal for loved ones living abroad, in care, in hospital or wherever physical delivery may not be practical.
            </p>
          </motion.div>
        </div>
      </section>
      )}

      {/* ── Spoken Echoes™ section removed: was competing with the "When words can't be said…" emotional moment ── */}

      {/* ── About + FAQ (compact SEO block) ── */}
      <section className="py-12 md:py-16" style={{ background: C.cream }}>
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          {/* About Sent2ment — heading + two-paragraph body. Hidden for the shortened
              launch homepage; code preserved below behind a false flag. */}
          {false && (
          <motion.section
            {...stagger(0.18)}
            data-testid="ltl-what-is-sent2ment"
            className="max-w-3xl mx-auto text-center"
            aria-labelledby="ltl-what-is-sent2ment-heading"
          >
            <p
              className="text-[10px] sm:text-[11px] tracking-[0.34em] uppercase font-semibold mb-5"
              style={{ color: C.gold }}
            >
              About Sent2ment
            </p>
            <h2
              id="ltl-what-is-sent2ment-heading"
              className="text-2xl sm:text-3xl md:text-4xl font-light leading-[1.2] tracking-[0.005em] mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
              data-testid="ltl-about-heading"
            >
              Preserving what matters most.
            </h2>
            <p
              className="text-sm sm:text-base leading-relaxed mx-auto mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec, maxWidth: '34em' }}
              data-testid="ltl-about-body-1"
            >
              Sent2ment<sup style={{ fontSize: '0.6em', verticalAlign: 'super' }}>&reg;</sup> creates meaningful keepsakes that preserve the words, voices and moments that matter most.
            </p>
            <p
              className="text-sm sm:text-base leading-relaxed mx-auto"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec, maxWidth: '34em' }}
              data-testid="ltl-about-body-2"
            >
              Through thoughtful design and deeply personal experiences, we create lasting ways for people to feel close &mdash; across time, distance and every chapter of life.
            </p>
          </motion.section>
          )}

          {/* Mini FAQ — collapsed by default for a lighter visual weight */}
          <motion.div {...stagger(0.15)} className="mt-12 max-w-xl mx-auto" data-testid="ltl-mini-faq">
            {[
              { q: 'How does it work?', a: 'After purchase, you\'ll be guided to upload your message — whether that\'s a voice note, video, or written words. This is then securely linked to your private QR code.' },
              { q: 'Is my message safe?', a: 'Yes. Your content is stored securely and only accessible through your unique QR code. We never share or access your personal messages. An additional passcode is optional — you can add one for an extra layer of privacy, if that feels right for you.' },
              { q: 'When will I receive my order?', a: 'Our delivery times are 7–10 days, however in busy periods it may take a little longer as every keepsake is made to order with precision and care.' },
            ].map((faq) => (
              <details
                key={faq.q}
                className="group py-4"
                style={{ borderBottom: `1px solid ${C.gold}22` }}
              >
                <summary
                  className="flex items-center justify-between cursor-pointer list-none text-sm sm:text-base font-light tracking-[0.01em] transition-colors duration-300"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
                >
                  <span>{faq.q}</span>
                  <span
                    aria-hidden="true"
                    className="text-xs transition-transform duration-300 group-open:rotate-45 ml-4"
                    style={{ color: C.gold }}
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: C.darkTextSec }}>
                  {faq.a}
                </p>
              </details>
            ))}
          </motion.div>

          {/* Legal links removed from here — same links now live within the dark footer directly below. */}
        </div>
      </section>

      {/* ── Brand-world statement — hidden for the shortened launch homepage.
          Code preserved below behind a false flag so it can be restored later. */}
      {false && (
      <section className="py-20 md:py-24" style={{ background: C.cream }} data-testid="ltl-brand-world">
        <div className="max-w-xl mx-auto px-6 md:px-12 text-center">
          <p
            className="text-[10px] sm:text-[11px] tracking-[0.34em] uppercase font-semibold mb-5"
            style={{ color: C.gold }}
            data-testid="ltl-brand-world-eyebrow"
          >
            The Timeless Collection
          </p>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-light leading-[1.2] tracking-[0.005em] mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}
            data-testid="ltl-brand-world-heading"
          >
            For Every Chapter.
          </h2>
          <p
            className="text-base sm:text-lg leading-relaxed mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkTextSec, maxWidth: '28em' }}
            data-testid="ltl-brand-world-body"
          >
            A growing collection of thoughtfully created keepsakes, each designed to preserve what matters most.
          </p>
        </div>
      </section>
      )}

      {/* ── Footer ── */}
      <footer className="py-16 md:py-28 rounded-t-[3rem] sm:rounded-t-[4rem] md:rounded-t-[6rem]" style={{ background: C.midnight, borderTop: `1px solid ${C.gold}30` }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <img
            src="/sent2ment-logo.png"
            alt="Sent2ment"
            className="h-16 sm:h-20 w-auto object-contain mx-auto mb-6"
            data-testid="ltl-footer-logo"
          />
          {/* "The Timeless Collection / For Every Chapter" footer heading removed
              for the shortened Letters That Last™-only launch. Contact + legal
              details below remain unchanged. */}
          <div className="max-w-md mx-auto mb-8" data-testid="ltl-footer-contact">
            <p
              className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase font-semibold mb-3"
              style={{ color: C.gold }}
            >
              Contact
            </p>
            <a
              href="mailto:hello@sent2ment.co.uk"
              className="inline-block text-base sm:text-lg italic mb-3 transition-opacity duration-300 hover:opacity-80"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}
              data-testid="ltl-footer-contact-email"
            >
              hello@sent2ment.co.uk
            </a>
            <p
              className="text-xs sm:text-sm italic leading-relaxed"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: `${C.cream}CC` }}
              data-testid="ltl-footer-contact-supporting"
            >
              We aim to respond within one working day.
            </p>
          </div>
          <div className="flex justify-center gap-5 mb-8" data-testid="ltl-footer-social">
            <a href="https://instagram.com/sent2ment_ltd" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="transition-colors duration-300 hover:opacity-80" style={{ color: C.gold }} data-testid="ltl-footer-instagram">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </a>
            <a href="https://www.tiktok.com/@sent2ment" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="transition-colors duration-300 hover:opacity-80" style={{ color: C.gold }} data-testid="ltl-footer-tiktok">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13a8.28 8.28 0 005.58 2.16V11.7a4.83 4.83 0 01-3.58-1.42V6.69h3.58z"/></svg>
            </a>
          </div>
          <p className="text-xs tracking-wide mb-3" style={{ color: C.cream }}>&copy; 2026 Sent2ment<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>&reg;</sup> Ltd. All rights reserved.</p>
          <p className="text-[11px] tracking-wide leading-relaxed mb-1.5" style={{ color: `${C.cream}B0` }} data-testid="ltl-footer-trademark">
            Sent2ment<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>&reg;</sup> is a registered trademark in the United Kingdom.
          </p>
          <p className="text-[11px] tracking-wide leading-relaxed mb-3" style={{ color: `${C.cream}B0` }} data-testid="ltl-footer-company">
            Company registered in England and Wales. Company No: 13190042
          </p>
          <p className="text-[10px] italic leading-relaxed max-w-lg mx-auto mb-6" style={{ color: `${C.cream}90`, fontFamily: "'Cormorant Garamond', serif" }} data-testid="ltl-footer-ip">
            Sent2ment<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>&reg;</sup>, Letters That Last<sup style={{ fontSize: '0.55em', verticalAlign: 'super', marginLeft: '0.05em' }}>&trade;</sup> and Spoken Echoes<sup style={{ fontSize: '0.55em', verticalAlign: 'super', marginLeft: '0.05em' }}>&trade;</sup> are names used by Sent2ment Ltd. Rights are reserved where applicable.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap" data-testid="ltl-footer-utility-row">
            <a href="/lp-privacy" target="_blank" rel="noopener noreferrer" className="text-xs transition-colors duration-300 hover:text-[#D4B87A]" style={{ color: `${C.cream}CC` }} data-testid="ltl-footer-privacy">Privacy Policy</a>
            <span className="text-xs" style={{ color: `${C.cream}55` }}>|</span>
            <a href="/lp-terms" target="_blank" rel="noopener noreferrer" className="text-xs transition-colors duration-300 hover:text-[#D4B87A]" style={{ color: `${C.cream}CC` }} data-testid="ltl-footer-terms">Terms &amp; Conditions</a>
            <span className="text-xs" style={{ color: `${C.cream}55` }}>|</span>
            <a href="/lp-contact" target="_blank" rel="noopener noreferrer" className="text-xs transition-colors duration-300 hover:text-[#D4B87A]" style={{ color: `${C.cream}CC` }} data-testid="ltl-footer-contact-link">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LettersLandingPage;
